import { CloudStore } from "@omni/types/api/store/cloud/store.model";
import { useCallback } from "react";

import { useStorePickerContext } from "@/components/Store/StorePicker/storePicker.context";
import { StorePickerType } from "@/components/Store/StorePicker/types/storePickerType.enum";
import { getStoreInfo } from "@/components/Store/utils/storeInfo";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { shouldDisableAddToCart } from "@/utils/disableAddToCart";
import {
    getProductAvailabilityStatus,
    ProductAvailabilityStatus
} from "@/utils/productAvailability";

import { StoreCardComponent } from "./storeCard.component";

type StoreCardContainerProps = {
    store: CloudStore;
};

const StoreCardContainer = ({ store }: StoreCardContainerProps) => {
    const { t } = useGranite();
    const { selectedStore, setSelectedStore, config, submitWithStore } =
        useStorePickerContext();

    const isDisabled = useCallback(() => {
        if (!config.displayStockLevel) return false;

        const productAvailability = store?.productAvailability;
        if (!productAvailability) return true;

        const productAvailabilityStatus =
            getProductAvailabilityStatus(productAvailability);
        const disableAddToCart = shouldDisableAddToCart(productAvailability);

        return (
            disableAddToCart ||
            productAvailabilityStatus === ProductAvailabilityStatus.NoStock
        );
    }, [config, store]);

    const address = `${store.address.streetName} ${store.address.streetNumber}, ${store.address.postalCode} ${store.address.town}`;

    const selected = store.code === selectedStore?.code;

    const handleOnStoreClick = () => {
        if (isDisabled()) {
            return;
        }
        if (config.type === StorePickerType.SELECT) {
            handleSubmit();
        }
        if (config.type !== StorePickerType.INFO) {
            setSelectedStore(store);
        }
    };

    const handleSubmit = () => {
        submitWithStore(store);
    };

    const getOpenInfo = useCallback(
        (storeDetails: CloudStore) => getStoreInfo(storeDetails, t),
        [t]
    );

    const storeStatus = getOpenInfo(store);

    const statusText = t(
        `form.searchstore.storeCard.status.${storeStatus.isOpen ? "open" : "closed"}`
    );

    const isDisabledStore = isDisabled();

    return (
        <StoreCardComponent
            storeName={store.shopName}
            isDisabled={isDisabledStore}
            isSelected={selected}
            handleOnStoreClick={handleOnStoreClick}
            handleSubmit={handleSubmit}
            storeStatus={storeStatus}
            statusText={statusText}
            address={address}
            productAvailability={store.productAvailability}
            config={config}
        />
    );
};

export { StoreCardContainer };
